import {
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Chart as ChartJS,
  ChartData,
  ScriptableContext,
} from "chart.js";
import classNames from "classnames";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { TypedChartComponent } from "react-chartjs-2/dist/types";

import AscHeading from "../AscTypography/AscHeading/AscHeading";
import styles from "./AscTeacherBaselineAssessmentChart.module.scss";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import { AdminChevronDownIcon } from "../AscIcons";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface inputSelectItem {
  value: string;
  label: string;
}

export interface AscTeacherBaselineAssessmentChartProps {
  className?: string;
  data: {
    skillName: string;
    globalAveragePercentage: number;
    scorePercentage?: number;
    variant?: number;
  }[];
  showSchoolAverage?: boolean;
  yourScoreLabel?: string;
  globalAverageLabel?: string;
  variantLabel?: string;
  variant?: boolean;
  title?: string;
  subTitle?: string;
  loading?: boolean;
  actionButtonText?: string;
  actionButtonPath?: string;
  batchesOptions?: inputSelectItem[];
  setAge?: (val: string[]) => void;
  setBatches?: (val: string[]) => void;
  setCountry?: (val: string[]) => void;
  countries?: inputSelectItem[];
}

const ageOptions = [
  {
    value: "13-14",
    label: "13-14",
  },
  {
    value: "15-16",
    label: "15-16",
  },
  {
    value: "17-18",
    label: "17-18",
  },
  {
    value: "19-20",
    label: "19-20",
  },
  {
    value: "21",
    label: "21",
  },
  {
    value: "All",
    label: "All",
  },
];

export default function AscTeacherBaselineAssessmentChart({
  className,
  data: _dataProp,
  yourScoreLabel = "School Average",
  globalAverageLabel = "Global Average",
  variantLabel = "Variant",
  variant = false,
  title = "Pillar Proficiency",
  subTitle = "Aggregated Pillar Performance",
  loading = false,
  showSchoolAverage = true,
  batchesOptions,
  countries = [],
  setAge,
  setBatches,
  setCountry,
}: AscTeacherBaselineAssessmentChartProps) {
  const data: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels: _dataProp?.map((d) => d.skillName) ?? [],
      datasets: [
        ...(_dataProp?.map((d, index) => {
          const data = new Array(_dataProp?.length).fill(0);
          data.splice(index, 1, d?.variant?.toFixed(2));
          return variant
            ? {
                label: variantLabel,
                data,
                backgroundColor: linearGradient("#CCF1FA", "#69D7EF"),
                stack: "Stack 0",
              }
            : {
                label: variantLabel,
                data,
                backgroundColor: linearGradient("#CCF1FA", "#69D7EF"),
              };
        }) ?? []),
        ...(_dataProp?.map((d, index) => {
          const data = new Array(_dataProp?.length).fill(0);
          data.splice(index, 1, d?.globalAveragePercentage?.toFixed(2));
          return {
            label: globalAverageLabel,
            data,
            backgroundColor: linearGradient("#FFE9CF", "#ff9b04"),
            order:
              index * 2 +
              (d?.globalAveragePercentage > (d?.scorePercentage || 0) ? 1 : 0),
          };
        }) ?? []),
        ...(_dataProp?.length && showSchoolAverage
          ? _dataProp.map((d, index) => {
              const data = new Array(_dataProp?.length).fill(0);
              data.splice(index, 1, d?.scorePercentage?.toFixed(2));
              return {
                label: yourScoreLabel,
                data,
                backgroundColor: linearGradient("#C3B6FD", "#4239fb"),
                order:
                  index * 2 +
                  ((d?.scorePercentage || 0) > d?.globalAveragePercentage
                    ? 1
                    : 0),
              };
            })
          : []),
      ],
    }),
    [_dataProp, globalAverageLabel, variantLabel, yourScoreLabel]
  );
  const options: Parameters<TypedChartComponent<"bar">>[0]["options"] = useMemo(
    () => ({
      type: "bar",
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";

              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += `${context.parsed.y}%`;
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            color: "#5E5E73",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
        y: {
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            // Include a percentage sign in the ticks
            callback: function (value) {
              return `${value}%`;
            },
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
          max: 100,
        },
      },
      size: {
        width: "100%",
      },
    }),
    []
  );

  const inputSelects = (
    name: string,
    placeholder: string,
    onChange: any,
    options?: inputSelectItem[]
  ) => {
    const handleChange = (values: string[] | undefined) =>
      values && onChange && onChange(values);

    return (
      <AscSelectInput
        className={styles.input}
        id={name}
        name={name}
        placeholder={placeholder}
        arrowIcon={<AdminChevronDownIcon />}
        onChange={handleChange}
        multiple
        inputWrapperClassName={styles.inputWrapper}
        multipleOptionSelectionDisplayClassName={
          styles.multipleOptionSelectionDisplay
        }
        optionsTagClassName={styles.optionsTag}
        hidden
      >
        {options?.map((option) => (
          <AscSelectInput.Option
            key={option.value}
            value={option.value}
            label={option.label}
          />
        ))}
      </AscSelectInput>
    );
  };

  return (
    <div
      data-testid="asc-baseline-assessment-chart"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
        },
        className
      )}
    >
      <header className={styles.header}>
        <div className={styles.top}>
          <AscHeading className={styles.title} level={3}>
            {title}
          </AscHeading>

          {(setAge || setBatches || setCountry) && (
            <div className={styles.filterWrapper}>
              {inputSelects("age", "Age", setAge, ageOptions)}
              {inputSelects("batch", "Batch", setBatches, batchesOptions)}
              {inputSelects("country", "Country", setCountry, countries)}
            </div>
          )}
        </div>

        <div className={styles.legend}>
          {showSchoolAverage ? (
            <div className={styles.legendItem}>
              <div
                className={styles.legendItemColor}
                style={{
                  background:
                    "linear-gradient(360deg, #C3B6FD -50%, #4239fb 150%)",
                }}
              />
              <div className={styles.legendItemLabel}>{yourScoreLabel}</div>
            </div>
          ) : null}
          <div className={styles.legendItem}>
            <div
              className={styles.legendItemColor}
              style={{
                background:
                  "linear-gradient(360deg, #FFE9CF -50%, #ff9b04 150%)",
              }}
            />
            <div className={styles.legendItemLabel}>{globalAverageLabel}</div>
          </div>

          {variant && (
            <div className={styles.legendItem}>
              <div
                className={styles.legendItemColor}
                style={{
                  background:
                    "linear-gradient(0deg, #FFF -45.91%, #61D5EE 100%)",
                }}
              />
              <div className={styles.legendItemLabel}>{variantLabel}</div>
            </div>
          )}
        </div>

        <AscHeading className={styles.subTitle} level={4}>
          {subTitle}
        </AscHeading>
      </header>
      <Bar options={options} data={data} />
    </div>
  );
}

function linearGradient(startColor: string, endColor: string) {
  return function (context: ScriptableContext<"bar">) {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if (!chartArea) {
      // This case happens on initial chart load
      return;
    }
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  };
}
